import { useMemo, useState } from "react";
import { differenceInYears } from "date-fns";

import { Button } from "components/ui/Button";
import { Checkbox } from "components/ui/Checkbox";
import { Dialog } from "components/ui/Dialog";
import { usePatientData } from "features/patients/hooks/usePatientData";

import styles from "./CHA2DS2VASCCalculatorModal.module.css";

const RISK_FACTORS = [
  { key: "chf", label: "Congestive Heart Failure (or LVSD)", points: 1 },
  { key: "hypertension", label: "Hypertension", points: 1 },
  { key: "age75plus", label: "Age ≥ 75 years", points: 2 },
  { key: "diabetes", label: "Diabetes Mellitus", points: 1 },
  { key: "stroke", label: "Prior Stroke / TIA / Thromboembolism", points: 2 },
  { key: "vascular", label: "Vascular Disease (prior MI, peripheral artery disease, aortic plaque)", points: 1 },
  { key: "age65to74", label: "Age 65–74 years", points: 1 },
  { key: "female", label: "Female sex", points: 1 },
];

const getPatientAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  return differenceInYears(new Date(), new Date(dateOfBirth));
};

// Risk interpretation per HSE CDM dataset source (clincalc): 0 = low, 1 = moderate, ≥2 = high.
const getRiskInterpretation = (score) => {
  if (score === 0) return "Low risk of stroke";
  if (score === 1) return "Moderate risk of stroke";
  return "High risk of stroke";
};

export const CHA2DS2VASCCalculatorModal = ({
  isOpen,
  onClose,
  onConfirm,
  prefillFactors,
}) => {
  const { data: patient } = usePatientData();
  const patientAge = getPatientAge(patient?.dateOfBirth);
  const isFemale = patient?.gender === "FEMALE";

  const getInitialFactors = () => {
    const initial = prefillFactors ?? {};

    if (patientAge !== null && !("age75plus" in initial) && !("age65to74" in initial)) {
      if (patientAge >= 75) initial.age75plus = true;
      else if (patientAge >= 65) initial.age65to74 = true;
    }

    if (isFemale && !("female" in initial)) {
      initial.female = true;
    }

    return initial;
  };

  const [checkedFactors, setCheckedFactors] = useState(getInitialFactors);

  const score = useMemo(
    () =>
      RISK_FACTORS.reduce(
        (total, { key, points }) => (checkedFactors[key] ? total + points : total),
        0
      ),
    [checkedFactors]
  );

  const scoreColorClass =
    score === 0 ? styles.scoreLow : score === 1 ? styles.scoreWarning : styles.scoreHigh;

  const handleToggle = (key) => {
    setCheckedFactors((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleClose = () => {
    onClose();
  };

  const handleConfirm = () => {
    onConfirm(String(score), checkedFactors);
    handleClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      title="CHA2DS2-VASc Score Calculator"
      width={580}
    >
      <div className={styles.body}>
        <div className={styles.factors}>
          {RISK_FACTORS.map(({ key, label, points }) => (
            <label key={key} className={styles.factorRow}>
              <Checkbox
                checked={!!checkedFactors[key]}
                onChange={() => handleToggle(key)}
              />
              <span className={styles.factorLabel}>{label}</span>
              <span className={styles.factorPoints}>+{points}</span>
            </label>
          ))}
        </div>

        <div className={`${styles.result} ${scoreColorClass}`}>
          <div className={styles.resultInfo}>
            <span className={styles.resultLabel}>CHA2DS2-VASc Score</span>
            <span className={styles.resultInterpretation}>
              {getRiskInterpretation(score)}
            </span>
          </div>
          <span className={`${styles.resultScore} ${scoreColorClass}`}>
            {score}/9
          </span>
        </div>

        <div className={styles.actions}>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirm}>Use score</Button>
        </div>
      </div>
    </Dialog>
  );
};
